import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useAuth } from "../features/auth/AuthProvider";
import { useNotify } from "../lib/notify";
import {
  useTeamMembers,
  useInviteTeamMember,
  useUpdateTeamMember,
  useDeleteTeamMember,
  type TeamMember,
} from "../features/team/api";

type Role = "sales" | "manager" | "admin";

export default function TeamPage() {
  const { current } = useWorkspace();
  const { user } = useAuth();

  // Team management is manager+ only. Sales reps who reach /team directly (URL
  // or bookmark) are bounced to their dashboard; the nav item is hidden too.
  // Gate via a flag so the hooks below still run unconditionally.
  const denied = !!user && !user.isSuperadmin && user.role === "sales";

  const workspaceId = denied ? undefined : current?.id;
  const { data, isLoading, isError, error } = useTeamMembers(workspaceId);

  // Admins (and superadmins) can manage every row. Managers can invite new
  // members and manage existing non-admin rows. Sales is read-only.
  const isAdminLevel =
    !!user && (user.isSuperadmin || user.role === "admin");
  const isManagerLevel =
    !!user && (isAdminLevel || user.role === "manager");
  const canInvite = isManagerLevel;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TeamMember | null>(null);
  const [pendingDeactivate, setPendingDeactivate] =
    useState<TeamMember | null>(null);

  const notify = useNotify();
  const update = useUpdateTeamMember(workspaceId);
  const remove = useDeleteTeamMember(workspaceId);

  if (denied) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">Team</h1>
        <p className="page-subtitle">
          {current ? current.name : "Pick a workspace to begin"}
        </p>
      </header>

      <Card
        title="Members"
        right={
          canInvite && workspaceId ? (
            <Button type="button" onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="skeleton-rows">Loading…</div>
        ) : isError ? (
          <div className="form-error">{(error as Error).message}</div>
        ) : (data?.users ?? []).length === 0 ? (
          <p className="muted">
            No members yet. Send an invite to get started.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  {isManagerLevel ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {(data?.users ?? []).map((m) => {
                  const isSelf = m.id === user?.id;
                  // Admins can act on anyone but themselves. Managers can act
                  // on non-admin rows only.
                  const canActOnRow =
                    !isSelf &&
                    (isAdminLevel ||
                      (isManagerLevel && m.role !== "admin"));
                  return (
                    <tr key={m.id}>
                      <td>{m.name}</td>
                      <td>{m.email}</td>
                      <td>
                        {canActOnRow ? (
                          <select
                            className="field-input"
                            value={m.role}
                            disabled={update.isPending}
                            onChange={(e) =>
                              update.mutate({
                                id: m.id,
                                role: e.target.value as Role,
                              })
                            }
                          >
                            <option value="sales">sales</option>
                            <option value="manager">manager</option>
                            {isAdminLevel ? (
                              <option value="admin">admin</option>
                            ) : null}
                          </select>
                        ) : (
                          <span className="badge">{m.role}</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${m.isActive ? "" : "badge-muted"}`}
                        >
                          {m.isActive ? "active" : "deactivated"}
                        </span>
                      </td>
                      <td>
                        {m.lastLoginAt
                          ? new Date(m.lastLoginAt).toLocaleString()
                          : "—"}
                      </td>
                      {isManagerLevel ? (
                        <td>
                          {canActOnRow ? (
                            <div className="row-actions">
                              {m.isActive ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setPendingDeactivate(m)}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={update.isPending}
                                  onClick={() =>
                                    update.mutate({ id: m.id, isActive: true })
                                  }
                                >
                                  Reactivate
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="danger"
                                onClick={() => setPendingDelete(m)}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {update.isError ? (
          <div className="form-error">{(update.error as Error).message}</div>
        ) : null}
        {remove.isError ? (
          <div className="form-error">{(remove.error as Error).message}</div>
        ) : null}
      </Card>

      {inviteOpen && workspaceId ? (
        <InviteDialog
          workspaceId={workspaceId}
          allowAdminRole={isAdminLevel}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        open={!!pendingDeactivate}
        title="Deactivate member?"
        body={
          pendingDeactivate ? (
            <>
              <strong>{pendingDeactivate.email}</strong> won't be able to log
              in. You can reactivate anytime.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        destructive
        busy={update.isPending}
        onConfirm={() => {
          if (!pendingDeactivate) return;
          const target = pendingDeactivate;
          update.mutate(
            { id: target.id, isActive: false },
            {
              onSuccess: () => notify.success(`${target.email} deactivated`),
              onError: (err) => notify.error(err),
              onSettled: () => setPendingDeactivate(null),
            }
          );
        }}
        onCancel={() => setPendingDeactivate(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove member?"
        body={
          pendingDelete ? (
            <>
              <strong>{pendingDelete.email}</strong> will be soft-deleted. They
              lose access immediately. Reversible by a superadmin via SQL.
            </>
          ) : null
        }
        confirmLabel="Remove"
        destructive
        busy={remove.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          remove.mutate(target.id, {
            onSuccess: () => notify.success(`${target.email} removed`),
            onError: (err) => notify.error(err),
            onSettled: () => setPendingDelete(null),
          });
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function InviteDialog({
  workspaceId,
  allowAdminRole,
  onClose,
}: {
  workspaceId: string;
  allowAdminRole: boolean;
  onClose: () => void;
}) {
  const invite = useInviteTeamMember(workspaceId);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("sales");

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="modal-title">Invite a team member</h2>
        <form
          className="form-stack"
          onSubmit={async (e) => {
            e.preventDefault();
            await invite.mutateAsync({ email, role });
            setEmail("");
          }}
        >
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label className="field">
            <span className="field-label">Role</span>
            <select
              className="field-input"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              <option value="sales">sales</option>
              <option value="manager">manager</option>
              {allowAdminRole ? <option value="admin">admin</option> : null}
            </select>
          </label>
          {invite.isError ? (
            <div className="form-error">{(invite.error as Error).message}</div>
          ) : null}
          {invite.isSuccess && invite.data ? (
            <div className="form-info">
              ✅ Invite sent. Accept URL:
              <pre className="codeblock">{invite.data.acceptUrl}</pre>
            </div>
          ) : null}
          <div className="dialog-actions">
            <Button
              type="button"
              variant="ghost"
              disabled={invite.isPending}
              onClick={onClose}
            >
              Close
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
